import { useEffect, useState } from "react";
import { Brain, Play, Settings as SettingsIcon, History, CheckCircle2, XCircle, Sparkles, Activity, AlertTriangle, ShieldCheck, Zap, Target, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const SEVERITY_BADGE = {
  info: "bg-blue-500/15 border-blue-500/40 text-blue-300",
  warn: "bg-amber-500/15 border-amber-500/40 text-amber-300",
  critical: "bg-rose-500/15 border-rose-500/40 text-rose-300",
};

const DECISION_LABEL = {
  EXECUTE_PRICING_ADJUSTMENT: { ico: Target, label: "Aggiusta Prezzo", color: "text-emerald-400" },
  DISPATCH_MAINTENANCE: { ico: Activity, label: "Manutenzione", color: "text-amber-400" },
  REBALANCE_FLEET: { ico: RefreshCw, label: "Riequilibra Flotta", color: "text-cyan-400" },
  BLOCK_BOOKING: { ico: ShieldCheck, label: "Blocca Booking", color: "text-rose-400" },
  BOOST_MARKETING: { ico: Sparkles, label: "Boost Marketing", color: "text-fuchsia-400" },
  NO_ACTION: { ico: CheckCircle2, label: "Nessuna Azione", color: "text-slate-400" },
};

const fmtPct = (n) => (n == null ? "—" : `${Math.round(Number(n) * 100)}%`);
const fmtEur = (n) => (n == null ? "—" : `€${Number(n).toFixed(2)}`);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
};

export default function EioDashboard() {
  const [tab, setTab] = useState("overview");
  const [config, setConfig] = useState(null);
  const [context, setContext] = useState(null);
  const [history, setHistory] = useState({ items: [], total: 0, applied: 0 });
  const [actions, setActions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [dryRun, setDryRun] = useState(true);

  const loadAll = async () => {
    try {
      const [c, ctx, h, a] = await Promise.all([
        api.get("/eio/config"),
        api.get("/eio/context"),
        api.get("/eio/history?limit=50"),
        api.get("/eio/actions?limit=50"),
      ]);
      setConfig(c.data?.config || null);
      setContext(ctx.data?.context || null);
      setHistory(h.data || { items: [], total: 0, applied: 0 });
      setActions(a.data?.items || []);
    } catch (e) {
      toast.error("Errore caricamento EIO");
    }
  };

  useEffect(() => { loadAll(); }, []);

  const saveConfig = async () => {
    if (!config) return;
    setBusy(true);
    try {
      const r = await api.put("/eio/config", config);
      setConfig(r.data?.config || config);
      toast.success("Configurazione salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setBusy(false); }
  };

  const decideNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/eio/decide", { dry_run: dryRun, notes: notes || null });
      toast.success(`Decisione: ${r.data?.decision?.decision || "?"} (conf ${fmtPct(r.data?.decision?.confidence)})`);
      setNotes("");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
    } finally { setBusy(false); }
  };

  const applyDecision = async (id) => {
    setBusy(true);
    try {
      await api.post("/eio/apply", { decision_id: id, approve: true });
      toast.success("Decisione applicata");
      loadAll();
    } catch (e) { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  const rejectDecision = async (id) => {
    setBusy(true);
    try {
      await api.post("/eio/apply", { decision_id: id, approve: false });
      toast.info("Decisione rifiutata");
      loadAll();
    } catch (e) { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  const occ = context?.fleet?.occupancy_pct ?? 0;
  const occColor = occ >= 75 ? "text-emerald-400" : occ >= 40 ? "text-amber-400" : "text-rose-400";

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="eio-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white flex items-center gap-3">
            <Brain className="h-9 w-9 text-fuchsia-400" />
            FabriRent <span className="text-fuchsia-400">EIO</span> Engine
          </h1>
          <p className="text-slate-400 text-sm mt-1">Engine Intelligence Operations — il cervello autonomo del tuo autonoleggio.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadAll}
            className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 flex items-center gap-2 text-sm"
            data-testid="eio-refresh-btn"
          >
            <RefreshCw className="h-4 w-4" /> Aggiorna
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard label="Stato Engine" value={config?.enabled ? "ATTIVO" : "OFF"} accent={config?.enabled ? "text-emerald-400" : "text-slate-400"} icon={Zap} testid="eio-kpi-enabled" />
        <KpiCard label="Occupazione Flotta" value={`${occ}%`} accent={occColor} icon={Target} testid="eio-kpi-occupancy" />
        <KpiCard label="Auto Disponibili" value={context?.fleet?.available ?? "—"} accent="text-cyan-400" icon={Activity} testid="eio-kpi-available" />
        <KpiCard label="Decisioni Totali" value={history?.total ?? 0} accent="text-fuchsia-400" icon={History} testid="eio-kpi-total" />
        <KpiCard label="Decisioni Applicate" value={history?.applied ?? 0} accent="text-emerald-400" icon={CheckCircle2} testid="eio-kpi-applied" />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-slate-800">
        {[
          { id: "overview", label: "Overview & Trigger", icon: Brain },
          { id: "history", label: "Storico Decisioni", icon: History },
          { id: "actions", label: "Azioni Eseguite", icon: Activity },
          { id: "config", label: "Configurazione", icon: SettingsIcon },
        ].map((t) => {
          const Ico = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 transition-all ${tab === t.id ? "border-fuchsia-400 text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}
              data-testid={`eio-tab-${t.id}`}
            >
              <Ico className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-fuchsia-400" /> Trigger Manuale
            </h3>
            <p className="text-xs text-slate-400 mb-3">Forza una decisione AI sui KPI live. Usa <b>dry-run</b> per simulare senza applicare.</p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Note opzionali per l'AI (es. 'weekend di Pasqua, atteso alto traffico')"
              className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-slate-200 text-sm min-h-[80px]"
              data-testid="eio-notes-input"
            />
            <label className="flex items-center gap-2 text-sm text-slate-300 mt-2 cursor-pointer">
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="eio-dryrun-checkbox" />
              Dry-run (simula senza applicare)
            </label>
            <button
              disabled={busy}
              onClick={decideNow}
              className="mt-3 w-full px-4 py-3 rounded-lg bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="eio-decide-btn"
            >
              <Play className="h-4 w-4" /> {busy ? "Analisi in corso…" : "Esegui Analisi AI"}
            </button>
          </div>

          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Activity className="h-5 w-5 text-cyan-400" /> Snapshot Live
            </h3>
            {context ? (
              <div className="space-y-2 text-sm">
                <Row k="Auto totali" v={context.fleet?.total} />
                <Row k="Disponibili / Noleggiate / Manut." v={`${context.fleet?.available} / ${context.fleet?.rented} / ${context.fleet?.maintenance}`} />
                <Row k="Contratti attivi" v={context.operations?.contracts_active} />
                <Row k="Booking prossimi 7gg" v={context.operations?.upcoming_bookings_7d} />
                <Row k="Review pending" v={context.operations?.review_pending} />
                <Row k="Alert manut. critici" v={context.operations?.critical_maintenance_alerts?.length} />
                <Row k="Clienti high-risk" v={context.risk?.high_risk_customers} />
                <Row k="Blacklist locale" v={context.risk?.blacklist_local} />
                <Row k="Multe aperte" v={context.operations?.multe_open} />
              </div>
            ) : <p className="text-slate-500 text-sm">Caricamento…</p>}
          </div>
        </div>
      )}

      {tab === "history" && (
        <div className="space-y-3">
          {history.items.length === 0 && (
            <div className="text-center py-16 text-slate-500 border border-dashed border-slate-800 rounded-xl">
              Nessuna decisione registrata. Premi <b>Esegui Analisi AI</b> in Overview.
            </div>
          )}
          {history.items.map((d) => {
            const meta = DECISION_LABEL[d.decision] || DECISION_LABEL.NO_ACTION;
            const Ico = meta.ico;
            return (
              <div key={d.id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4" data-testid={`eio-decision-${d.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div className="flex items-start gap-3 flex-1">
                    <Ico className={`h-6 w-6 ${meta.color} flex-shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`font-semibold ${meta.color}`}>{meta.label}</span>
                        <span className={`text-xs px-2 py-0.5 rounded border ${SEVERITY_BADGE[d.alert_severity] || SEVERITY_BADGE.info}`}>
                          {d.alert_severity}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">
                          conf {fmtPct(d.confidence)}
                        </span>
                        {d.applied ? (
                          <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/40 text-emerald-300">applicata</span>
                        ) : d.dry_run ? (
                          <span className="text-xs px-2 py-0.5 rounded bg-blue-500/10 border border-blue-500/40 text-blue-300">dry-run</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/40 text-amber-300">in attesa</span>
                        )}
                      </div>
                      <p className="text-slate-300 text-sm mt-1.5">{d.rationale}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400 mt-2">
                        {d.target_category && <span>Categoria: <b className="text-slate-200">{d.target_category}</b></span>}
                        {d.new_price != null && <span>Nuovo prezzo: <b className="text-emerald-300">{fmtEur(d.new_price)}</b></span>}
                        {d.logistics_action && <span>Logistica: <b className="text-slate-200">{d.logistics_action}</b></span>}
                        <span>{fmtDate(d.created_at)}</span>
                        <span>fonte: {d.source}</span>
                      </div>
                      {Array.isArray(d.safety_notes) && d.safety_notes.length > 0 && (
                        <div className="mt-2 text-xs text-amber-300 flex items-start gap-1.5">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                          <span>{d.safety_notes.join(" · ")}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {!d.applied && d.decision !== "NO_ACTION" && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => applyDecision(d.id)}
                        className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs flex items-center gap-1"
                        data-testid={`eio-apply-${d.id}`}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" /> Approva
                      </button>
                      <button
                        onClick={() => rejectDecision(d.id)}
                        className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs flex items-center gap-1"
                        data-testid={`eio-reject-${d.id}`}
                      >
                        <XCircle className="h-3.5 w-3.5" /> Rifiuta
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "actions" && (
        <div className="space-y-2">
          {actions.length === 0 ? (
            <div className="text-center py-16 text-slate-500 border border-dashed border-slate-800 rounded-xl">Nessuna azione eseguita finora.</div>
          ) : actions.map((a) => (
            <div key={a.id} className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-fuchsia-400" />
                <span className="text-slate-200 font-medium">{a.kind}</span>
                {a.category && <span className="text-xs text-slate-400">cat: {a.category}</span>}
                {a.new_price != null && <span className="text-xs text-emerald-400">→ {fmtEur(a.new_price)}</span>}
                {a.matched != null && <span className="text-xs text-cyan-400">{a.matched} auto aggiornate</span>}
                {a.error && <span className="text-xs text-rose-400">err: {a.error}</span>}
              </div>
              <span className="text-xs text-slate-500">{fmtDate(a.created_at)}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "config" && config && (
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 max-w-2xl">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <SettingsIcon className="h-5 w-5 text-fuchsia-400" /> Configurazione Engine
          </h3>
          <div className="space-y-4">
            <label className="flex items-center justify-between gap-4 p-3 bg-slate-950/60 rounded-lg border border-slate-800 cursor-pointer">
              <div>
                <div className="text-slate-200 font-medium">Engine attivo (scheduler autonomo)</div>
                <div className="text-xs text-slate-400">Quando attivo, l&apos;AI gira ogni N minuti e applica decisioni con confidence ≥ soglia.</div>
              </div>
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                className="h-5 w-5"
                data-testid="eio-config-enabled"
              />
            </label>

            <Field label="Intervallo (minuti)" hint="Frequenza di esecuzione automatica (5–720)">
              <input
                type="number" min={5} max={720}
                value={config.interval_minutes}
                onChange={(e) => setConfig({ ...config, interval_minutes: Number(e.target.value) })}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-slate-200"
                data-testid="eio-config-interval"
              />
            </Field>

            <Field label="Soglia auto-apply (confidence)" hint="Le decisioni sotto questa soglia restano in attesa di approvazione manuale (0.50–1.00)">
              <input
                type="number" step="0.05" min={0.5} max={1}
                value={config.auto_apply_threshold}
                onChange={(e) => setConfig({ ...config, auto_apply_threshold: Number(e.target.value) })}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-slate-200"
                data-testid="eio-config-threshold"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Prezzo minimo (€)" hint="Floor assoluto">
                <input type="number" min={1} value={config.min_price_eur}
                  onChange={(e) => setConfig({ ...config, min_price_eur: Number(e.target.value) })}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-slate-200"
                  data-testid="eio-config-min-price" />
              </Field>
              <Field label="Prezzo massimo (€)" hint="Cap assoluto">
                <input type="number" value={config.max_price_eur}
                  onChange={(e) => setConfig({ ...config, max_price_eur: Number(e.target.value) })}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-slate-200"
                  data-testid="eio-config-max-price" />
              </Field>
            </div>

            <Field label={`Delta massimo per categoria (${Math.round(config.max_pricing_delta_pct * 100)}%)`} hint="Limite di scostamento dal prezzo medio della categoria (5–80%)">
              <input
                type="range" min={0.05} max={0.80} step={0.05}
                value={config.max_pricing_delta_pct}
                onChange={(e) => setConfig({ ...config, max_pricing_delta_pct: Number(e.target.value) })}
                className="w-full"
                data-testid="eio-config-delta"
              />
            </Field>

            <button
              disabled={busy}
              onClick={saveConfig}
              className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-fuchsia-600 to-purple-600 hover:from-fuchsia-500 hover:to-purple-500 text-white font-semibold disabled:opacity-50"
              data-testid="eio-save-config-btn"
            >
              Salva configurazione
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const KpiCard = ({ label, value, accent, icon: Icon, testid }) => (
  <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
      {Icon && <Icon className={`h-4 w-4 ${accent}`} />}
    </div>
    <div className={`text-2xl font-bold mt-2 ${accent}`}>{value}</div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between border-b border-slate-800/60 pb-1.5">
    <span className="text-slate-400">{k}</span>
    <span className="text-slate-200 font-medium">{v ?? "—"}</span>
  </div>
);

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm text-slate-300 font-medium mb-1">{label}</label>
    {hint && <div className="text-xs text-slate-500 mb-1.5">{hint}</div>}
    {children}
  </div>
);
